import { RASHI_NAMES } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { arcPath } from './geometry';

/**
 * The South Indian 4×4 grid, annotated: every outer cell permanently one
 * rashi, the lagna marked with its traditional diagonal stroke (here in
 * Mesha as an example), and an arrow showing the clockwise count.
 */

const CELL = 75;

/** rashi index (0 = Mesha) → [col, row], the fixed South Indian arrangement. */
const RASHI_CELL: [number, number][] = [
  [1, 0], // Mesha
  [2, 0], // Vrishabha
  [3, 0], // Mithuna
  [3, 1], // Karka
  [3, 2], // Simha
  [3, 3], // Kanya
  [2, 3], // Tula
  [1, 3], // Vrischika
  [0, 3], // Dhanu
  [0, 2], // Makara
  [0, 1], // Kumbha
  [0, 0], // Meena
];

export function SouthLayout() {
  const { t } = useI18n();
  return (
    <svg
      viewBox="0 0 300 324"
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={t('learn.fig.southLayout.alt')}
    >
      <defs>
        <marker
          id="south-clockwise"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="7"
          markerHeight="7"
          orient="auto"
        >
          <path d="M 0 0 L 8 4 L 0 8 Z" fill="var(--mode-ink)" />
        </marker>
      </defs>

      {RASHI_NAMES.map((name, i) => {
        const [col, row] = RASHI_CELL[i];
        const x = col * CELL;
        const y = row * CELL;
        const isLagna = i === 0; // Mesha as the worked example
        return (
          <g key={name}>
            <rect
              x={x}
              y={y}
              width={CELL}
              height={CELL}
              fill={isLagna ? 'var(--mode-soft)' : 'transparent'}
              stroke="var(--border-strong)"
              strokeWidth="1"
            />
            <text
              x={x + CELL / 2}
              y={y + CELL / 2 + (isLagna ? -2 : 3)}
              textAnchor="middle"
              fontSize="9.5"
              fill={isLagna ? 'var(--mode-ink)' : 'var(--text)'}
              fontWeight={isLagna ? 700 : 400}
            >
              {name}
            </text>
            {isLagna && (
              <>
                {/* The traditional diagonal stroke that marks the lagna. */}
                <line
                  x1={x + 2}
                  y1={y + 16}
                  x2={x + 16}
                  y2={y + 2}
                  stroke="var(--mode-ink)"
                  strokeWidth="1.5"
                />
                <text
                  x={x + CELL / 2}
                  y={y + CELL / 2 + 13}
                  textAnchor="middle"
                  fontSize="8.5"
                  fill="var(--mode-ink)"
                >
                  {t('learn.fig.lagna')}
                </text>
              </>
            )}
          </g>
        );
      })}

      {/* Houses count clockwise from the marked cell. */}
      <path
        d={arcPath(150, 150, 44, -30, 60, 1)}
        fill="none"
        stroke="var(--mode-ink)"
        strokeWidth="1.5"
        markerEnd="url(#south-clockwise)"
      />
      <text x={150} y={175} textAnchor="middle" fontSize="9" fill="var(--text-faint)">
        1 → 2 → 3
      </text>

      <text x={150} y={316} textAnchor="middle" fontSize="10" fill="var(--text-dim)">
        {t('learn.fig.southLayout.note')}
      </text>
    </svg>
  );
}
